import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'

export default function ShiftModal({
  shift,
  date,
  sectorId,
  sectors,
  users,
  existingShifts = [],
  onClose,
  onSave,
  onEditShift,
  onDeleteShift,
  canEdit,
  canDelete,
  canCreate
}) {
  const [formData, setFormData] = useState({
    sectorId: sectorId || '',
    userId: '',
    date: date || '',
    startTime: '09:00',
    endTime: '17:00',
    note: '',
    status: 'scheduled',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (shift) {
      setFormData({
        sectorId: shift.sectorId || '',
        userId: shift.userId || '',
        date: shift.date || '',
        startTime: shift.startTime?.slice(0, 5) || '09:00',
        endTime: shift.endTime?.slice(0, 5) || '17:00',
        note: shift.note || '',
        status: shift.status || 'scheduled',
      })
    } else if (date) {
      setFormData(prev => ({ ...prev, date }))
    }
  }, [shift, date])

  useEffect(() => {
    if (sectorId && !shift) {
      setFormData(prev => ({ ...prev, sectorId }))
    }
  }, [sectorId, shift])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError(null)
  }

  const validate = () => {
    if (!formData.sectorId) return 'Seleziona un settore'
    if (!formData.userId) return 'Seleziona una persona'
    if (!formData.date) return 'Seleziona una data'
    if (!formData.startTime) return 'Inserisci ora di inizio'
    if (!formData.endTime) return 'Inserisci ora di fine'
    if (formData.startTime >= formData.endTime) {
      return "L'ora di fine deve essere successiva all'ora di inizio"
    }
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setError(null)
    setLoading(true)

    try {
      await onSave(formData)
      toast.success(shift ? 'Turno aggiornato' : 'Turno creato', {
        position: 'top-center',
        autoClose: 2000,
      })
    } catch (err) {
      const msg = err.response?.data?.error || 'Errore nel salvataggio'
      setError(msg)
      toast.error(msg, { position: 'top-center' })
    } finally {
      setLoading(false)
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div className="shift-modal-backdrop" onClick={handleBackdropClick}>
      <div className="shift-modal-content">
        <button type="button" className="shift-modal-close" onClick={onClose}>
          <i className="fas fa-times" />
        </button>

        <h3 className="shift-modal-title">
          <i className={shift ? 'fas fa-edit' : 'fas fa-plus-circle'} />
          {shift ? ' Modifica Turno' : ' Nuovo Turno'}
        </h3>

        {/* Preview turni esistenti */}
        {existingShifts.length > 0 && !shift && (
          <div className="existing-shifts-preview">
            <h4 className="preview-title">
              <i className="fas fa-list" /> Turni esistenti per questa data
            </h4>
            <div className="preview-list">
              {existingShifts.map(s => {
                const userName = s.user?.fullName || s.user?.email || 'N/A'
                const sectorName = s.sector?.name || 'N/A'
                const sectorColor = s.sector?.color || '#dc3545'
                return (
                  <div key={s.id} className={`preview-item status-${s.status}`}>
                    <div
                      className="preview-item__sector"
                      style={{ backgroundColor: sectorColor }}
                      title={sectorName}
                    >
                      <i className={s.sector?.icon || 'fas fa-tag'} />
                    </div>
                    <div className="preview-item__info">
                      <strong>{userName}</strong>
                      <span className="preview-item__time">
                        {s.startTime?.slice(0, 5)} - {s.endTime?.slice(0, 5)}
                      </span>
                    </div>
                    <div className="preview-item__actions">
                      {canEdit && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => onEditShift?.(s)}
                          title="Modifica"
                        >
                          <i className="fas fa-edit" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost btn-danger"
                          onClick={() => onDeleteShift?.(s.id)}
                          title="Elimina"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-circle" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="shift-modal-form">
          <div className="form-row">
            <label htmlFor="sectorId">
              <i className="fas fa-tag" /> Settore <span className="req">*</span>
            </label>
            <select
              id="sectorId"
              name="sectorId"
              value={formData.sectorId}
              onChange={handleChange}
              required
            >
              <option value="">Seleziona settore...</option>
              {sectors.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label htmlFor="userId">
              <i className="fas fa-user" /> Persona <span className="req">*</span>
            </label>
            <select
              id="userId"
              name="userId"
              value={formData.userId}
              onChange={handleChange}
              required
            >
              <option value="">Seleziona persona...</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.fullName || u.email}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label htmlFor="date">
              <i className="fas fa-calendar" /> Data <span className="req">*</span>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row-grid">
            <div className="form-row">
              <label htmlFor="startTime">
                <i className="fas fa-hourglass-start" /> Inizio <span className="req">*</span>
              </label>
              <input
                type="time"
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-row">
              <label htmlFor="endTime">
                <i className="fas fa-hourglass-end" /> Fine <span className="req">*</span>
              </label>
              <input
                type="time"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <label htmlFor="status">
              <i className="fas fa-flag" /> Stato
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="scheduled">Programmato</option>
              <option value="confirmed">Confermato</option>
              <option value="completed">Completato</option>
              <option value="cancelled">Annullato</option>
            </select>
          </div>

          <div className="form-row">
            <label htmlFor="note">
              <i className="fas fa-sticky-note" /> Note
            </label>
            <textarea
              id="note"
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
              placeholder="Note aggiuntive..."
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={onClose}
              disabled={loading}
            >
              <i className="fas fa-times" /> Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Salvataggio...
                </>
              ) : (
                <>
                  <i className="fas fa-save" /> {shift ? 'Aggiorna' : 'Crea'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
